import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { ProgressSpinner } from "primereact/progressspinner";
import BarLineChart from "components/BarLineChart";
import Button from "components/Buttons/Button";
import CopyField from "components/CopyField";
import EmptyState from "components/EmptyState";
import PeriodSelector, {
  DateRangeValue,
  PeriodPreset,
} from "components/PeriodSelector/PeriodSelector";
import Sidebar from "components/Sidebar";
import SummaryMetricCard from "components/SummaryMetricCard";
import SuccessModal from "components/Modals/SuccessModal/SuccessModal";
import WithdrawModal from "components/WithdrawModal/WithdrawModal";
import { useNotify } from "context/NotificationContext";
import {
  createBalanceOrder,
  fetchReferralData,
  fetchReferralLink,
} from "services/referralService/referralService";
import type { ReferralResponse } from "services/referralService/referralService.types";
import { logger } from "utils/logger";
import {
  BalanceRow,
  BalanceValue,
  ChartBody,
  ChartCard,
  ChartEmptyWrapper,
  ChartHeader,
  ChartNote,
  ChartTitle,
  ChartToolbar,
  ContentWrapper,
  ErrorWrapper,
  Legend,
  LegendItem,
  LegendSwatch,
  LoadingContainer,
  MetricsGrid,
  ReferralPageContainer,
  SectionTitle,
  SurfaceCard,
  Title,
  TopGrid,
} from "./ReferralPage.styles";
import {
  aggregateReferralData,
  formatReferralConversion,
  formatReferralMoney,
  formatReferralNumber,
  formatReferralPeriod,
  getReferralBarLabel,
  getReferralBarMaxSize,
  getReferralChartTitle,
  getReferralEarliestDate,
  getReferralPresetRange,
} from "./referral.utils";

const ReferralPage: React.FC = () => {
  const toast = useNotify();
  const [referralData, setReferralData] = useState<ReferralResponse | null>(null);
  const [referralLink, setReferralLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("365");
  const [period, setPeriod] = useState<DateRangeValue>(() =>
    getReferralPresetRange("365", null, new Date()),
  );
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isSubmittingWithdraw, setIsSubmittingWithdraw] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const maxDate = useMemo(() => new Date(), []);

  const loadReferralPage = useCallback(async () => {
    setLoading(true);
    setHasError(false);

    const userId = Number(Cookies.get("user_id"));
    const dataPromise = fetchReferralData();
    const linkPromise = Number.isFinite(userId) && userId > 0
      ? fetchReferralLink(userId)
      : Promise.reject(new Error("User ID not found in cookies"));

    const [dataResult, linkResult] = await Promise.allSettled([dataPromise, linkPromise]);

    if (dataResult.status === "rejected") {
      logger.error("Ошибка при загрузке данных по рефералам:", dataResult.reason);
      setHasError(true);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось загрузить данные реферальной программы",
        life: 3000,
      });
    } else {
      setReferralData(dataResult.value);
    }

    if (linkResult.status === "fulfilled") {
      setReferralLink(linkResult.value);
    } else {
      logger.error("Ошибка при получении реферальной ссылки:", linkResult.reason);
      setReferralLink("");
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось получить реферальную ссылку",
        life: 3000,
      });
    }

    setLoading(false);
  }, [toast]);

  useEffect(() => {
    void loadReferralPage();
  }, [loadReferralPage]);

  const dailyData = useMemo(
    () => referralData?.referral_data ?? [],
    [referralData],
  );
  const earliestDate = useMemo(() => getReferralEarliestDate(dailyData), [dailyData]);
  const aggregated = useMemo(
    () => aggregateReferralData(dailyData, period),
    [dailyData, period],
  );
  const conversion = useMemo(
    () => formatReferralConversion(
      referralData?.number_of_referrals ?? 0,
      referralData?.number_of_clicks ?? 0,
    ),
    [referralData],
  );
  const chartData = useMemo(
    () => aggregated.points.map((point) => ({
      label: point.label,
      barValue: point.joined,
      lineValue: point.total,
    })),
    [aggregated.points],
  );

  const handlePresetChange = (preset: Exclude<PeriodPreset, "custom">) => {
    setPeriodPreset(preset);
    setPeriod(getReferralPresetRange(preset, earliestDate, maxDate));
  };

  const handleWithdraw = async (data: { amount: string; cardNumber: string }) => {
    setIsSubmittingWithdraw(true);
    try {
      await createBalanceOrder({
        requisites: data.cardNumber,
        balance_sum: Number(data.amount),
      });
      setIsWithdrawOpen(false);
      setSuccessMessage(
        `Заявка создана: ${formatReferralMoney(Number(data.amount))} · реквизиты •••• ${data.cardNumber.slice(-4)}`,
      );
    } catch (error) {
      logger.error("Ошибка при создании заявки на вывод средств:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать заявку на вывод средств",
        life: 3000,
      });
    } finally {
      setIsSubmittingWithdraw(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        </LoadingContainer>
      );
    }

    if (hasError || !referralData) {
      return (
        <ErrorWrapper>
          <EmptyState
            title="Реферальная программа не загрузилась"
            description="Попробуйте повторить запрос."
            action={
              <Button variant="secondary" size="md" onClick={loadReferralPage}>
                Повторить
              </Button>
            }
          />
        </ErrorWrapper>
      );
    }

    const barLabel = getReferralBarLabel(aggregated.granularity);

    return (
      <>
        <TopGrid>
          <SurfaceCard>
            <SectionTitle>Ваша ссылка</SectionTitle>
            <CopyField
              value={referralLink}
              ariaLabel="Реферальная ссылка"
              buttonVariant="panelPrimary"
              disabled={!referralLink}
              onCopyError={(error) => {
                logger.error("Ошибка копирования реферальной ссылки:", error);
                toast.show({
                  severity: "error",
                  summary: "Ошибка",
                  detail: "Не удалось скопировать ссылку",
                  life: 3000,
                });
              }}
            />
          </SurfaceCard>

          <SurfaceCard>
            <SectionTitle>Баланс</SectionTitle>
            <BalanceRow>
              <BalanceValue>{formatReferralMoney(referralData.balance)}</BalanceValue>
              <Button
                variant="panelPrimary"
                size="md"
                onClick={() => setIsWithdrawOpen(true)}
                disabled={referralData.balance <= 0}
              >
                Вывести средства
              </Button>
            </BalanceRow>
          </SurfaceCard>
        </TopGrid>

        <MetricsGrid>
          <SummaryMetricCard
            label="Переходы по ссылке"
            value={formatReferralNumber(referralData.number_of_clicks)}
            note="за всё время"
          />
          <SummaryMetricCard
            label="Зарегистрировались"
            value={formatReferralNumber(referralData.number_of_referrals)}
            note={
              <>
                <strong>{formatReferralNumber(aggregated.referralsInPeriod)}</strong> за выбранный период
              </>
            }
          />
          <SummaryMetricCard
            label="Доходит до регистрации"
            value={conversion.label}
            progress={conversion.value}
          />
          <SummaryMetricCard
            label="Заработано"
            value={formatReferralMoney(referralData.total_earned)}
            note="за всё время"
          />
        </MetricsGrid>

        <ChartCard>
          <ChartHeader>
            <div>
              <ChartTitle>{getReferralChartTitle(aggregated.granularity)}</ChartTitle>
              <Legend>
                <LegendItem><LegendSwatch $kind="bar" />{barLabel}</LegendItem>
                <LegendItem><LegendSwatch $kind="line" />всего (накопительно)</LegendItem>
              </Legend>
            </div>
            <ChartToolbar>
              <PeriodSelector
                value={periodPreset}
                range={period}
                minDate={earliestDate ?? undefined}
                maxDate={maxDate}
                onPresetChange={handlePresetChange}
                onCustomApply={(range) => {
                  setPeriodPreset("custom");
                  setPeriod(range);
                }}
              />
            </ChartToolbar>
          </ChartHeader>

          <ChartBody>
            {dailyData.length ? (
              <BarLineChart
                data={chartData}
                barLabel={barLabel}
                lineLabel="Всего приглашено"
                formatValue={formatReferralNumber}
                barMaxSize={getReferralBarMaxSize(aggregated.granularity)}
                ariaLabel="Динамика приглашённых учеников за выбранный период"
              />
            ) : (
              <ChartEmptyWrapper>
                <EmptyState
                  title="Приглашённых учеников пока нет"
                  description="Когда кто-то зарегистрируется по вашей ссылке, здесь появится динамика."
                />
              </ChartEmptyWrapper>
            )}
          </ChartBody>
          <ChartNote>Период: {formatReferralPeriod(period)}</ChartNote>
        </ChartCard>
      </>
    );
  };

  return (
    <ReferralPageContainer>
      <Sidebar />
      <ContentWrapper>
        <Title>Реферальная программа</Title>
        {renderContent()}
      </ContentWrapper>

      {isWithdrawOpen && referralData && (
        <WithdrawModal
          availableAmount={referralData.balance}
          onSubmit={handleWithdraw}
          onCancel={() => setIsWithdrawOpen(false)}
          isSubmitting={isSubmittingWithdraw}
        />
      )}

      {successMessage && (
        <SuccessModal
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
    </ReferralPageContainer>
  );
};

export default ReferralPage;
